import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

import { Button, Card, CardHeader, Input, Select } from '@/components/common';
import {
  useProfile,
  useUpdateProfileSettings,
  useUploadProfilePicture,
} from '@/hooks/useSettings';
import { digitsOnlyOnChange, nameOnChange } from '@/utils/validation';
import { downscaleImage, isTooLarge } from '@/utils/imageDownscale';
import { getErrorMessage } from '@/services/api';

/**
 * POSP #2 — the ONE account-profile surface, shared by everyone who has one.
 *
 * This is the Profile tab of `/settings`, lifted out of `SettingsPage` whole:
 * the avatar uploader (`POST /users/upload_picture/` -> `User.profile_picture`)
 * and the editable `User` fields (`PATCH /users/me/`). Nothing about it is
 * role-specific, which is precisely the point — "My Agent Profile" used to show
 * an agent their name, phone and email READ-ONLY, three clicks away from the
 * page where the very same values were editable, and the POSP page repeated
 * them a second time in its own "Contact Information" card.
 *
 * So it is merged rather than duplicated: one component, one save path, two
 * places that mount it. The agent's profile page now holds the whole profile,
 * avatar included, and `/settings` keeps the tab it has always had.
 *
 * These fields are also what an agent's CUSTOMERS see as their policy agent:
 * `/customers/me/` answers a POSP login with `posp_name` / `posp_phone` /
 * `posp_email`, read straight off `POSP.user` (apps/customers/views.py) — the
 * same `User` row this form writes. Hence `infoFootnote`, which the agent page
 * uses to say so.
 */

const profileSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  phone: z.string().optional(),
  date_of_birth: z.string().optional(),
  gender: z.enum(['male', 'female', 'other']).optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

export interface AccountProfileSectionProps {
  /** Heading over the editable fields. The agent portal calls these facts
   *  "Contact Information", which is what its users look for; `/settings`
   *  calls them "Profile Information". Same card either way. */
  infoTitle?: string;
  infoSubtitle?: string;
  /** Optional line under the form, e.g. who else sees these values. */
  infoFootnote?: string;
}

export function AccountProfileSection({
  infoTitle = 'Profile Information',
  infoSubtitle = 'Update your personal information',
  infoFootnote,
}: AccountProfileSectionProps) {
  const { data: profile, isLoading, isError, refetch } = useProfile();
  const updateProfileMutation = useUpdateProfileSettings();
  const uploadPictureMutation = useUploadProfilePicture();
  /** 0-99 while a photo is uploading, null when idle. */
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      phone: profile?.phone || '',
      date_of_birth: profile?.date_of_birth || '',
      gender: profile?.gender || undefined,
    },
  });

  // Update form when profile loads
  if (profile && profileForm.getValues('first_name') !== profile.first_name) {
    profileForm.reset({
      first_name: profile.first_name,
      last_name: profile.last_name,
      phone: profile.phone || '',
      date_of_birth: profile.date_of_birth || '',
      gender: profile.gender || undefined,
    });
  }

  const onProfileSubmit = (data: ProfileFormData) => {
    updateProfileMutation.mutate(data);
  };

  const handlePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Clear the input so the same file can be re-picked after a failure --
    // otherwise onChange never fires again for it.
    e.target.value = '';

    if (isTooLarge(file)) {
      toast.error('That image is larger than 25MB. Please choose a smaller one.');
      return;
    }

    try {
      // Shrinking here is the actual fix for the 30s timeout: an 8MB phone
      // photo becomes a couple of hundred KB, so the upload finishes instead of
      // merely being given longer to fail.
      const prepared = await downscaleImage(file);
      const formData = new FormData();
      formData.append('profile_picture', prepared);
      setUploadPercent(0);
      await uploadPictureMutation.mutateAsync({ formData, onProgress: setUploadPercent });
    } catch (error) {
      // The mutation's own onError already toasts a failed request; this
      // catches the decode/encode path, which it never sees.
      if (!(error instanceof Error) || !error.message.includes('status code')) {
        toast.error(getErrorMessage(error));
      }
    } finally {
      setUploadPercent(null);
    }
  };

  // Three states, never two: a profile that failed to load must not render as
  // an empty form, because saving that form would overwrite the real values
  // with blanks.
  if (isError) {
    return (
      <Card>
        <CardHeader title={infoTitle} subtitle={infoSubtitle} />
        <div className="py-6 text-center">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            Your profile could not be loaded
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Nothing here can be saved until it loads, so the form is not shown.
          </p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Picture */}
      <Card>
        <CardHeader title="Profile Picture" subtitle="Upload your profile photo or logo" />
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
            {profile?.profile_picture ? (
              <img src={profile.profile_picture} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <UserCircleIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            )}
          </div>
          <div>
            <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors">
              {uploadPictureMutation.isPending ? 'Uploading...' : 'Upload Photo'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePictureUpload}
                disabled={uploadPictureMutation.isPending}
              />
            </label>
            {uploadPercent !== null && (
              <div className="mt-3 w-48" role="progressbar" aria-valuenow={uploadPercent}>
                <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div
                    className="h-full bg-primary-600 transition-[width] duration-200"
                    style={{ width: `${uploadPercent}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Uploading… {uploadPercent}%
                </p>
              </div>
            )}
            {/* "Max 2MB" was never the real limit -- nginx accepts 25M
                on this vhost, and large photos are now shrunk in the
                browser before they are sent. */}
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              JPG, PNG, GIF or WebP. Large photos are resized automatically.
            </p>
          </div>
        </div>
      </Card>

      {/* Profile Info */}
      <Card>
        <CardHeader title={infoTitle} subtitle={infoSubtitle} />
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 rounded bg-gray-100 dark:bg-gray-700" />
            ))}
          </div>
        ) : (
          <form onSubmit={profileForm.handleSubmit(onProfileSubmit)}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Input
                label="First Name"
                error={profileForm.formState.errors.first_name?.message}
                required
                {...profileForm.register('first_name', { onChange: nameOnChange })}
              />
              <Input
                label="Last Name"
                error={profileForm.formState.errors.last_name?.message}
                required
                {...profileForm.register('last_name', { onChange: nameOnChange })}
              />
              <Input
                label="Phone"
                inputMode="numeric"
                maxLength={10}
                error={profileForm.formState.errors.phone?.message}
                {...profileForm.register('phone', { onChange: digitsOnlyOnChange })}
              />
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">{profile?.email || '-'}</p>
              </div>
              <Input
                label="Date of Birth"
                type="date"
                error={profileForm.formState.errors.date_of_birth?.message}
                {...profileForm.register('date_of_birth')}
              />
              <Select
                label="Gender"
                options={genderOptions}
                placeholder="Select..."
                error={profileForm.formState.errors.gender?.message}
                {...profileForm.register('gender')}
              />
            </div>
            {infoFootnote && <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">{infoFootnote}</p>}
            <div className="mt-6 flex justify-end">
              <Button type="submit" isLoading={updateProfileMutation.isPending}>
                Save Changes
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
